/**
 * 性能优化工具函数
 * Performance optimization utilities
 */

/**
 * 节流函数 - 限制函数在指定时间内只执行一次
 * @param {Function} func - 要节流的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function} 节流后的函数
 */
export function throttle(func, delay = 300) {
  let timer = null
  let lastRun = 0
  
  return function (...args) {
    const now = Date.now()
    
    if (now - lastRun >= delay) {
      func.apply(this, args)
      lastRun = now
    } else {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func.apply(this, args)
        lastRun = Date.now()
      }, delay - (now - lastRun))
    }
  }
}

/**
 * 防抖函数 - 延迟执行函数，如果在延迟期间再次触发则重新计时
 * @param {Function} func - 要防抖的函数
 * @param {number} delay - 延迟时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, delay = 300) {
  let timer = null
  
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

/**
 * 请求动画帧节流
 * @param {Function} func - 要执行的函数
 * @returns {Function} 节流后的函数
 */
export function rafThrottle(func) {
  let rafId = null
  let lastArgs = null
  
  return function (...args) {
    lastArgs = args
    
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        func.apply(this, lastArgs)
        rafId = null
      })
    }
  }
}

/**
 * 预加载图片
 * @param {string[]} urls - 图片 URL 数组
 * @returns {Promise<void>}
 */
export function preloadImages(urls) {
  return Promise.all(
    urls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(url)
        img.onerror = () => reject(url)
        img.src = url
      })
    })
  )
}

/**
 * 预加载关键资源
 * @param {string} url - 资源 URL
 * @param {string} type - 资源类型 (script, style, font, image)
 */
export function preloadResource(url, type = 'script') {
  const link = document.createElement('link')
  link.rel = 'preload'
  link.href = url
  link.as = type
  
  if (type === 'font') {
    link.crossOrigin = 'anonymous'
  }
  
  document.head.appendChild(link)
}

/**
 * 检测浏览器是否支持 WebP
 * @returns {Promise<boolean>}
 */
export function checkWebPSupport() {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img.width === 1)
    img.onerror = () => resolve(false)
    img.src = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA='
  })
}

/**
 * 获取优化后的图片 URL（根据设备像素比和屏幕尺寸）
 * @param {string} baseUrl - 基础图片 URL
 * @param {object} options - 选项
 * @returns {string} 优化后的图片 URL
 */
export function getOptimizedImageUrl(baseUrl, options = {}) {
  const {
    width = window.innerWidth
    // quality = 80,
    // format = 'auto'
  } = options
  
  const dpr = window.devicePixelRatio || 1
  // const optimizedWidth = Math.min(width * dpr, 2560) // 最大宽度限制
  
  // 如果有 CDN 或图片处理服务，可以在这里添加参数
  // 例如: return `${baseUrl}?w=${optimizedWidth}&q=${quality}&f=${format}`
  
  // 当前版本：直接返回原始 URL
  // 未来可以根据 width, quality, format 参数进行图片优化
  console.log('Image optimization params:', { width, dpr })
  
  return baseUrl
}

/**
 * 监听页面可见性变化
 * @param {Function} onVisible - 页面可见时的回调
 * @param {Function} onHidden - 页面隐藏时的回调
 */
export function onVisibilityChange(onVisible, onHidden) {
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      onHidden && onHidden()
    } else {
      onVisible && onVisible()
    }
  })
}

/**
 * 计算首屏加载时间
 * @returns {number} 首屏加载时间（毫秒）
 */
export function getFirstPaintTime() {
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing
    return timing.domContentLoadedEventEnd - timing.navigationStart
  }
  return 0
}

/**
 * 获取资源加载性能数据
 * @returns {Array} 资源加载数据
 */
export function getResourceTiming() {
  if (window.performance && window.performance.getEntriesByType) {
    return window.performance.getEntriesByType('resource')
  }
  return []
}

/**
 * 监控长任务（Long Tasks）
 * @param {Function} callback - 检测到长任务时的回调
 */
export function observeLongTasks(callback) {
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.duration > 50) { // 超过 50ms 的任务
            callback(entry)
          }
        }
      })
      
      observer.observe({ entryTypes: ['longtask'] })
      return observer
    } catch (e) {
      console.warn('Long tasks observation not supported', e)
    }
  }
  return null
}

/**
 * 优化滚动性能
 * @param {Function} callback - 滚动回调函数
 * @param {object} options - 选项
 * @returns {Function} 清理函数
 */
export function optimizedScroll(callback, options = {}) {
  const { throttleDelay = 100, useRaf = true } = options
  
  const handler = useRaf ? rafThrottle(callback) : throttle(callback, throttleDelay)
  
  window.addEventListener('scroll', handler, { passive: true })
  
  return () => {
    window.removeEventListener('scroll', handler)
  }
}

/**
 * 优化 Resize 事件
 * @param {Function} callback - resize 回调函数
 * @param {number} delay - 延迟时间
 * @returns {Function} 清理函数
 */
export function optimizedResize(callback, delay = 150) {
  const handler = debounce(callback, delay)
  
  window.addEventListener('resize', handler, { passive: true })
  
  return () => {
    window.removeEventListener('resize', handler)
  }
}

/**
 * 数字滚动动画
 * @param {HTMLElement} element - 目标元素
 * @param {number} target - 目标数字
 * @param {number} duration - 动画持续时间（毫秒）
 * @param {Function} formatter - 格式化函数
 */
export function animateNumber(element, target, duration = 2000, formatter = (n) => Math.floor(n)) {
  const start = 0
  const startTime = Date.now()
  
  function update() {
    const now = Date.now()
    const progress = Math.min((now - startTime) / duration, 1)
    
    // 使用 easeOutCubic 缓动函数
    const easeProgress = 1 - Math.pow(1 - progress, 3)
    const current = start + (target - start) * easeProgress
    
    element.textContent = formatter(current)
    
    if (progress < 1) {
      requestAnimationFrame(update)
    } else {
      element.textContent = formatter(target)
    }
  }
  
  requestAnimationFrame(update)
}

/**
 * 批量 DOM 操作优化
 * @param {Function} callback - DOM 操作函数
 */
export function batchDOMUpdate(callback) {
  requestAnimationFrame(() => {
    callback()
  })
}

/**
 * 检测网络连接质量
 * @returns {object} 网络信息
 */
export function getNetworkInfo() {
  if ('connection' in navigator) {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    return {
      effectiveType: conn.effectiveType, // 4g, 3g, 2g, slow-2g
      downlink: conn.downlink, // 下行速度 Mbps
      rtt: conn.rtt, // 往返时间 ms
      saveData: conn.saveData // 是否开启省流模式
    }
  }
  return null
}

/**
 * 根据网络质量调整资源加载策略
 * @returns {string} 'high' | 'medium' | 'low'
 */
export function getLoadStrategy() {
  const networkInfo = getNetworkInfo()
  
  if (!networkInfo) return 'high'
  
  if (networkInfo.saveData) return 'low'
  
  switch (networkInfo.effectiveType) {
    case '4g':
      return 'high'
    case '3g':
      return 'medium'
    case '2g':
    case 'slow-2g':
      return 'low'
    default:
      return 'medium'
  }
}

